import React from 'react';
import { Button } from '../ui/button';
import { DynamicTable } from '../common/DynamicTable';
import { Pencil, Trash2 } from 'lucide-react';

interface Empresa {
  id: string;
  nome: string;
  logoUrl?: string;
  corPrimaria?: string;
  corSecundaria?: string;
  ativo: boolean;
  dataCriacao: string;
  dataAtualizacao?: string;
  limiteInstancias?: number | null;
  limiteUsuarios?: number | null;
  _count?: {
    usuarios: number;
    contatos: number;
    tickets: number;
    instanciasEvolution?: number;
    instanciasUazapi?: number;
    instanciasMeta?: number;
  };
  configuracaoBackup?: {
    ativo: boolean;
    frequencia?: string;
    horario?: string;
    ultimoBackupEm?: string | null;
    ultimoBackupStatus?: 'SUCESSO' | 'ERRO' | null;
    ultimoBackupMensagem?: string | null;
  } | null;
  [key: string]: any; // For any additional dynamic properties
}

interface EmpresasTableProps {
  empresas: Empresa[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof Empresa, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  onRowClick: (row: Empresa) => void;
  onEdit: (empresa: Empresa) => void;
  onDelete: (empresa: Empresa) => void;
  canEdit: boolean;
}

export const EmpresasTable: React.FC<EmpresasTableProps> = ({
  empresas,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onRowClick,
  onEdit,
  onDelete,
  canEdit
}) => {
  // Uso atual / limite (limite null = ilimitado)
  const renderUso = (usado: number, limite?: number | null) => {
    if (limite == null) {
      return <span className="text-xs text-gray-600 dark:text-gray-300">{usado} / ∞</span>;
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${usado >= limite ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'}`}>
        {usado} / {limite}
      </span>
    );
  };

  const columns = [
    {
      id: 'nome',
      label: 'Nome',
      sortable: true
    },
    {
      id: 'logoUrl',
      label: 'Logo',
      align: 'center' as const,
      format: (value: string) => (
        value ? (
          <img
            src={`${((process.env.REACT_APP_API_URL || 'http://localhost:5001')?.replace(/\/api$/, ''))}${value.startsWith('/') ? '' : '/'}${value}`}
            className="w-16 h-16 object-contain mx-auto"
            alt="Logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = '<span class="text-gray-400 text-xs">Erro ao carregar</span>';
            }}
          />
        ) : (
          <span className="text-gray-400 text-xs">Sem logo</span>
        )
      )
    },
    {
      id: 'ativo',
      label: 'Status',
      align: 'center' as const,
      format: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      id: 'limiteUsuarios',
      label: 'Usuários',
      align: 'center' as const,
      sortable: false,
      format: (_: any, row: Empresa) => renderUso(row._count?.usuarios ?? 0, row.limiteUsuarios)
    },
    {
      id: 'limiteInstancias',
      label: 'Instâncias',
      align: 'center' as const,
      sortable: false,
      format: (_: any, row: Empresa) => renderUso(
        (row._count?.instanciasEvolution ?? 0) + (row._count?.instanciasUazapi ?? 0) + (row._count?.instanciasMeta ?? 0),
        row.limiteInstancias
      )
    },
    {
      id: 'dataCriacao',
      label: 'Criado em',
      format: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    {
      id: 'backupAutomatico',
      label: 'Backup Automático',
      align: 'center' as const,
      sortable: false,
      format: (_: any, row: Empresa) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.configuracaoBackup?.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-200'}`}>
          {row.configuracaoBackup?.ativo ? 'Configurado' : 'Não configurado'}
        </span>
      )
    },
    {
      id: 'backupUltimaExecucao',
      label: 'Última Execução',
      align: 'center' as const,
      sortable: false,
      format: (_: any, row: Empresa) => {
        const config = row.configuracaoBackup;
        if (!config?.ultimoBackupEm) {
          return <span className="text-gray-400 text-xs">Nunca executado</span>;
        }
        const sucesso = config.ultimoBackupStatus === 'SUCESSO';
        const dataFormatada = new Date(config.ultimoBackupEm).toLocaleString('pt-BR');
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${sucesso ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}
            title={!sucesso && config.ultimoBackupMensagem ? config.ultimoBackupMensagem : dataFormatada}
          >
            {sucesso ? 'Sucesso' : 'Erro'} em {dataFormatada}
          </span>
        );
      }
    },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: any) => (
        <div className="flex justify-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={empresas}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      onRowClick={onRowClick}
      mobileCards
      searchPlaceholder="Pesquisar empresas..."
    />
  );
};
